"use client";

import React, { useState, ChangeEvent, FormEvent, useEffect, useCallback } from "react";
import styles from "../studentForm/StudentForm.module.css";
import Button from "../ui/Buttons/Buttons";
import ToggleSwitch from "../ui/ToggleSwitch/ToggleSwitch";
import { StudentFormProps, FormField, FormSection, FilePreview } from "./type";
import apiClient from "@/helpers/api.helper";
import { STUDENT_GETBUS_ROUTE } from "../../../constants/urls";
import type { BusRoute } from "@/models/api/busRoute";

/* -------- Field groups inside the single Services section -------- */
// const TRANSPORT_FIELD_IDS = ["busRoute", "busfee"];
const TRANSPORT_FIELD_IDS = ["busRoute", "pickup", "busfee"];
const CANTEEN_FIELD_IDS = ["canteenPlan", "canteenfee"];
const HOSTEL_FIELD_IDS = ["hostelName", "hostelfee"];

const StudentForm: React.FC<StudentFormProps> = ({
  enquiryData = {},
  formConfig = [],
  onSubmit,
  formTitle = "",
  onUploadDocuments,
  submitLabel = "",
  servicesEnabled = false,
  onToggleServices,
  canteenEnabled = false,
  onToggleCanteen,
  hostelEnabled = false,
  onToggleHostel,
}) => {
  const initializeOpenSections = useCallback(() => {
    return new Set(
      formConfig
        .filter((section) => !section.collapsible || section.defaultOpen)
        .map((section) => section.id)
    );
  }, [formConfig]);

  const [openSections, setOpenSections] = useState<Set<string>>(initializeOpenSections);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [filePreviews, setFilePreviews] = useState<Record<string, FilePreview[]>>({});
  // const PICKUPS = ["block 1", "block 2", "block 3"] as const;
  const [busRoutes, setBusRoutes] = useState<BusRoute[]>([]);
  const [routeOptions, setRouteOptions] = useState<string[]>([]);
  const [pickupOptions, setPickupOptions] = useState<string[]>([]);
  const getDefaultProfile = useCallback(() => {
    return (
      (enquiryData as any)?.profilePicture ||
      formConfig?.flatMap((s) => s.fields)?.find((f) => f.id === "profilePicture")?.placeholder ||
      ""
    );
  }, [enquiryData, formConfig]);

  const CANTEEN_PLAN_TO_FEE: Record<string, number> = {
    "Breakfast-Dinner": 15000,
    "Dinner": 7000,
  };

  const HOSTEL_NAME_TO_FEE: Record<string, number> = {
    Ganga: 10000,
    Indus: 13000,
  };

  const handleUploadDocumentsClick = useCallback(async () => {
    // Pull the first file (if any) from the file fields
    const tenth = Array.isArray(formData.tenthMarksheet) ? formData.tenthMarksheet[0] : formData.tenthMarksheet;
    const twelfth = Array.isArray(formData.twelfthMarksheet) ? formData.twelfthMarksheet[0] : formData.twelfthMarksheet;

    if (!tenth && !twelfth) {
      alert("Please choose a document to upload (10th and/or 12th).");
      return;
    }

    try {
      await onUploadDocuments?.({
        tenth: tenth instanceof File ? tenth : undefined,
        twelfth: twelfth instanceof File ? twelfth : undefined,
      });
    } catch (e: any) {
      alert(e?.response?.data?.message || "Failed to upload documents.");
    }
  }, [formData, onUploadDocuments]);

  const [preview, setPreview] = useState<string>(getDefaultProfile());
  // const ROUTE_TO_DEFAULT_PICKUP: Record<string, string> = {
  //   "route 1": "block 1",
  //   "route 2": "block 2",
  //   "route 3": "block 3",
  // };

  function collectAllFields(sections: FormSection[]): FormField[] {
    const out: FormField[] = [];
    const walk = (sec: FormSection) => {
      if (sec.fields?.length) out.push(...sec.fields);
      sec.subsections?.forEach(walk);
    };
    sections.forEach(walk);
    return out;
  }

  // --- helper: only count fees that are "active" given toggles ---
  function isActiveFee(fieldId: string) {
    if (fieldId === "busfee") return servicesEnabled;  // Transport toggle
    if (fieldId === "canteenfee") return canteenEnabled;
    if (fieldId === "hostelfee") return hostelEnabled;
    return true; // fees that are outside Services (if you add any later)
  }

  useEffect(() => {
    if (!canteenEnabled) {
      setFormData(prev => {
        const next = { ...prev };
        delete next.canteenPlan;
        delete next.canteenfee;
        return next;
      });
    }
  }, [canteenEnabled]);

  useEffect(() => {
    if (!hostelEnabled) {
      setFormData(prev => {
        const next = { ...prev };
        delete next.hostelName;
        delete next.hostelfee;
        return next;
      });
    }
  }, [hostelEnabled]);

  
  useEffect(() => {
    let additionalFeesPerMonth = 0;

    // Only count service fees if that service is enabled and the fee exists
    if (servicesEnabled && formData.busfee) {
      additionalFeesPerMonth += Number(formData.busfee) || 0;
    }
    if (canteenEnabled && formData.canteenfee) {
      additionalFeesPerMonth += Number(formData.canteenfee) || 0;
    }
    if (hostelEnabled && formData.hostelfee) {
      additionalFeesPerMonth += Number(formData.hostelfee) || 0;
    }

    const courseFees = Number(formData.courseFees) || 0;
    const courseFeesPerMonth = courseFees / 12; // Keep as decimal for calculation
    const totalFeesPerMonth = courseFeesPerMonth + additionalFeesPerMonth;

    // Calculate yearly total directly from course fees + (additional fees × 12)
    const totalFeesPerYear = courseFees + (additionalFeesPerMonth * 12);

    setFormData(prev => {
      if (
        prev.additionalFeesPerMonth === additionalFeesPerMonth &&
        prev.totalFeesPerMonth === Math.round(totalFeesPerMonth) && // Round only for display
        prev.totalFeesPerYear === totalFeesPerYear
      ) return prev;

      return {
        ...prev,
        additionalFeesPerMonth: additionalFeesPerMonth,
        totalFeesPerMonth: Math.round(totalFeesPerMonth), // Round only for display
        totalFeesPerYear: totalFeesPerYear,
      };
    });
  }, [
    servicesEnabled,
    canteenEnabled,
    hostelEnabled,
    formData.busfee,
    formData.canteenfee,
    formData.hostelfee,
    formData.courseFees
  ]);

  // prefill
  useEffect(() => {
    if (enquiryData && Object.keys(enquiryData).length > 0) {
      const prefilledData: Record<string, any> = {};
      formConfig.forEach((section) => {
        section.fields.forEach((field) => {
          const v = (enquiryData as any)[field.id];
          if (v !== undefined && v !== null && v !== "") {
            prefilledData[field.id] = v;
          }
        });
      });
      setFormData((prev) => ({ ...prev, ...prefilledData }));
      if ((enquiryData as any)?.profilePicture) setPreview((enquiryData as any).profilePicture);
    }
  }, [enquiryData, formConfig]);

  useEffect(() => {
    setOpenSections(initializeOpenSections());
  }, [formConfig, initializeOpenSections]);

  // Clear subsection data when toggles are OFF
  useEffect(() => {
    if (!servicesEnabled) {
      setFormData((prev) => {
        const next = { ...prev };
        TRANSPORT_FIELD_IDS.forEach((id) => delete next[id]);
        return next;
      });
    }
  }, [servicesEnabled]);

  // Fetch transport routes when transport is enabled
  useEffect(() => {
    if (!servicesEnabled) return;
    let cancelled = false;

    (async () => {
      try {
        const rows = await apiClient.get<BusRoute[]>(STUDENT_GETBUS_ROUTE);
        if (cancelled) return;

        const list = Array.isArray(rows) ? rows : [];
        setBusRoutes(list);

        // unique route names for the first dropdown
        const uniques = Array.from(new Set(list.map(r => r.routeName).filter(Boolean)));
        setRouteOptions(uniques);
      } catch {
        setBusRoutes([]);
        setRouteOptions([]);
        setPickupOptions([]);
      }
    })();

    return () => { cancelled = true; };
  }, [servicesEnabled]);

  // Route changed -> update pickup list and fee
  useEffect(() => {
    const selectedRoute = String(formData.busRoute || "");
    if (!selectedRoute) {
      setPickupOptions([]);
      setFormData(prev => ({ ...prev, pickup: "", busfee: "" }));
      return;
    }

    const rowsForRoute = busRoutes.filter(r => r.routeName === selectedRoute);
    const picks = rowsForRoute.map(r => r.pickupPoint).filter(Boolean);
    setPickupOptions(picks);

    if (!picks.includes(formData.pickup)) {
      const first = rowsForRoute[0];
      setFormData(prev => ({
        ...prev,
        pickup: first?.pickupPoint ?? "",
        busfee: first ? Number(first.fees ?? 0) : ""
      }));
    } else {
      const row = rowsForRoute.find(r => r.pickupPoint === formData.pickup);
      setFormData(prev => ({ ...prev, busfee: row ? Number(row.fees ?? 0) : "" }));
    }
  }, [formData.busRoute, busRoutes]);

  // Pickup changed -> sync fee
  useEffect(() => {
    const selectedRoute = String(formData.busRoute || "");
    const selectedPickup = String(formData.pickup || "");
    if (!selectedRoute || !selectedPickup) return;

    const row = busRoutes.find(
      r => r.routeName === selectedRoute && r.pickupPoint === selectedPickup
    );
    setFormData(prev => ({ ...prev, busfee: row ? Number(row.fees ?? 0) : "" }));
  }, [formData.pickup, formData.busRoute, busRoutes]);

  useEffect(() => {
    if (!canteenEnabled) {
      setFormData((prev) => {
        const next = { ...prev };
        CANTEEN_FIELD_IDS.forEach((id) => delete next[id]);
        return next;
      });
    }
  }, [canteenEnabled]);

  useEffect(() => {
    if (!hostelEnabled) {
      setFormData((prev) => {
        const next = { ...prev };
        HOSTEL_FIELD_IDS.forEach((id) => delete next[id]);
        return next;
      });
    }
  }, [hostelEnabled]);

  useEffect(() => {
    const feeFields = formConfig.flatMap(section =>
      section.fields.filter(f => f.isFee)
    );

    if (feeFields.length === 0) return;

    const totalMonth = feeFields.reduce(
      (sum, f) => sum + Number(formData[f.id] || 0),
      0
    );
    const totalYear = totalMonth * 12;

    setFormData(prev => {
      if (
        prev.totalFeesPerMonth === totalMonth &&
        prev.totalFeesPerYear === totalYear
      ) return prev;

      return { ...prev, totalFeesPerMonth: totalMonth, totalFeesPerYear: totalYear };
    });
  }, [formData, formConfig]);

  /* ---------- Field helpers ---------- */
  const handleFieldChange = useCallback((fieldId: string, value: any) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  const handleFileChange = useCallback((fieldId: string, files: FileList | null) => {
    if (!files || files.length === 0) return;
    const fileArray = Array.from(files);
    setFormData((prev) => ({ ...prev, [fieldId]: fileArray }));
    const previews: FilePreview[] = fileArray.map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
    }));
    setFilePreviews((prev) => ({ ...prev, [fieldId]: previews }));
  }, []);

  const handleImageChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setPreview(objectUrl);
      handleFileChange("profilePicture", e.target.files);
      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [handleFileChange]);

  const renderField = useCallback(
    (field: FormField) => {
      const isPrefilled =
        enquiryData &&
        (enquiryData as any)[field.id] !== undefined &&
        (enquiryData as any)[field.id] !== null &&
        (enquiryData as any)[field.id] !== "";

      const fieldValue = formData[field.id] ?? "";

      const commonProps = {
        id: field.id,
        name: field.id,
        required: field.required,
        placeholder: field.placeholder,
        readOnly: isPrefilled || field.readonly,
        disabled: isPrefilled,
        value: fieldValue,
        onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
          handleFieldChange(field.id, e.target.value),
        className: `${styles.inputField} ${isPrefilled ? styles.disabledField : ""}`,
        "aria-label": field.label,
        "aria-required": field.required,
      };

      switch (field.type) {
        case "textarea":
          return <textarea {...commonProps} rows={4} className={`${styles.textarea} ${commonProps.className}`} />;

        case "select": {
          const isRoute = field.id === "busRoute";
          const isCanteenPlan = field.id === "canteenPlan";
          const isHostelName = field.id === "hostelName";

          const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
            const val = e.target.value;

            if (isRoute) {
              // set route; pickup/fee will be handled by effects above
              handleFieldChange("busRoute", val);
              return;
            }

            if (isCanteenPlan) {
              handleFieldChange("canteenPlan", val);
              const price = CANTEEN_PLAN_TO_FEE[val] ?? 0;
              handleFieldChange("canteenfee", price || "");
              return;
            }

            if (isHostelName) {
              handleFieldChange("hostelName", val);
              const price = HOSTEL_NAME_TO_FEE[val] ?? 0;
              handleFieldChange("hostelfee", price || "");
              return;
            }

            handleFieldChange(field.id, val);
          };

          // Use dynamic options for transport fields
          const optionsToUse =
            field.id === "busRoute"
              ? routeOptions
              : field.id === "pickup"
                ? pickupOptions
                : (field.options || []);

          return (
            <select
              {...commonProps}
              onChange={handleSelectChange}
              value={String(fieldValue ?? "")}
              className={`${styles.select} ${commonProps.className}`}
            >
              <option value="">{field.placeholder || "Select"}</option>
              {optionsToUse.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          );
        }
        case "checkbox":
          return (
            <div className={styles.checkboxContainer}>
              <label
                htmlFor={field.id}
                className={styles.checkboxLabel}
              >
                {field.label}
                {field.required && <span className={styles.required}>*</span>}
              </label>
              <input
                type="checkbox"
                id={field.id}
                name={field.id}
                checked={!!fieldValue}
                onChange={(e) => handleFieldChange(field.id, e.target.checked)}
                className={styles.checkboxInput}
                required={field.required}
              />
            </div>
          );
        
        case "file":
          return (
            <div className={styles.fileUploadContainer}>
              <input
                type="file"
                id={field.id}
                accept={field.accept || "*"}
                multiple={field.multiple || false}
                required={field.required}
                onChange={(e) => handleFileChange(field.id, e.target.files)}
                className={styles.fileInput}
              />
            </div>
          );



        default:
          return <input type={field.type} {...commonProps} />;
      }
    },
    [enquiryData, formData, handleFieldChange, handleFileChange, routeOptions, pickupOptions]
  );

  const renderFieldsSubset = (section: FormSection, ids: string[]) => {
    return section.fields
      .filter((f) => ids.includes(f.id) && !["firstName", "middleName", "lastName", "profilePicture"].includes(f.id))
      .map((field) => (
        <div key={field.id} className={styles.inputGroup} style={{ gridColumn: `span ${field.columns || 1}` }}>
          <label htmlFor={field.id} className={styles.label}>
            {field.label}
            {field.required && <span className={styles.required}>*</span>}
          </label>
          {renderField(field)}
        </div>
      ));
  };

  /* ---------- Section helpers ---------- */
  const resetSectionFields = (section: FormSection, prevData: Record<string, any>) => {
    const next = { ...prevData };

    const resetValue = (field: FormField) => {
      switch (field.type) {
        case "number":
          return 0;
        case "checkbox":
          return false;
        case "file":
          return [];
        default:
          return "";
      }
    };

    section.fields.forEach((f) => {
      next[f.id] = resetValue(f);
    });

    section.subsections?.forEach((sub) => Object.assign(next, resetSectionFields(sub, next)));

    return next;
  };

  const handleSectionToggle = (section: FormSection, value: boolean) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (value) {
        next.add(section.id);
      } else {
        next.delete(section.id);
        setFormData((prevData) => resetSectionFields(section, prevData));
      }
      return next;
    });
  };

  const renderSection = useCallback(
    (section: FormSection, index?: number) => {
      const isServices = section.id === "services";
      const isOpen = isServices ? true : openSections.has(section.id);
      const showPhoto = section.id === "student-info";
      return (
        <section key={section.id} className={styles.section}>
          <header className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{section.title}</h2>


            {section.collapsible && !isServices && (
              <ToggleSwitch
                label=""
                value={isOpen}
                onToggle={(v) => handleSectionToggle(section, v)}
              />
            )}
          </header>

          {(isOpen || !section.collapsible) && (
            <>
              {/* Profile + name row for first section */}
              {showPhoto && (
                <div className={styles.flex}>
                  <div className={styles.imageSection}>
                    {/* <div className={styles.imagePreview}> */}
                    <div className={`${styles.imagePreview} ${styles.square}`}>
                      <img
                        src={preview || "/images/users/profile.png"}
                        alt="Profile preview"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).src = "/images/users/profile.png";
                        }}
                      />
                    </div>
                    <label className={styles.uploadBtn}>
                      {section.fields.find((f) => f.id === "profilePicture")?.label || "Upload Image"}
                      <input type="file" accept="image/*" hidden onChange={handleImageChange} />
                    </label>
                  </div>

                  <div className={styles.Name}>
                    {section.fields
                      .filter((f) => f.id === "firstName" || f.id === "lastName" || f.id === "middleName")
                      .map((field) => (
                        <div
                          key={field.id}
                          className={styles.inputGroup}
                          style={{ gridColumn: `span ${field.columns || 1}` }}
                        >
                          <label htmlFor={field.id} className={styles.label}>
                            {field.label}
                            {field.required && <span className={styles.required}>*</span>}
                          </label>
                          {renderField(field)}
                        </div>
                      ))}
                  </div>
                </div>
              )}

              
              <div
                className={styles.sectionContent}
                style={{ gridTemplateColumns: `repeat(${section.columns}, 1fr)` }}
              >
                {section.id !== "services" &&
                  section.fields
                    .filter((f) => !["firstName", "middleName", "lastName", "profilePicture"].includes(f.id))
                    .map((field) => (
                      <div
                        key={field.id}
                        className={styles.inputGroup}
                        style={{ gridColumn: `span ${field.columns || 1}` }}
                      >
                        <label htmlFor={field.id} className={styles.label}>
                          {field.label}
                          {field.required && <span className={styles.required}>*</span>}
                        </label>
                        {renderField(field)}
                      </div>
                    ))}

               
                {section.id === "documents" && (
                  <div className={styles.uploadRow}>
                    <button
                      type="button"                        // ← was "submit"
                      className={styles.uploadDocBtn}
                      onClick={handleUploadDocumentsClick} // ← NEW
                    >
                      Upload Document
                    </button>
                  </div>
                )}

                {section.id === "services" && (
                  <>
                    {servicesEnabled && renderFieldsSubset(section, TRANSPORT_FIELD_IDS)}
                    {canteenEnabled && renderFieldsSubset(section, CANTEEN_FIELD_IDS)}
                    {hostelEnabled && renderFieldsSubset(section, HOSTEL_FIELD_IDS)}
                  </>
                )}
              </div>

              {/* Nested subsections */}
              {section.subsections?.map((sub, idx) => renderSection(sub, idx))}
            </>
          )}
        </section>
      );
    },
    [openSections, preview, renderField, servicesEnabled, canteenEnabled, hostelEnabled]
  );

  /* ---------- Submit ---------- */
  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();

      const requiredFields = formConfig.flatMap((section) => {
        if (section.id !== "services") {
          return section.fields.filter((f) => f.required).map((f) => f.id);
        }
        return section.fields
          .filter((f) => {
            if (TRANSPORT_FIELD_IDS.includes(f.id)) return servicesEnabled;
            if (CANTEEN_FIELD_IDS.includes(f.id)) return canteenEnabled;
            if (HOSTEL_FIELD_IDS.includes(f.id)) return hostelEnabled;
            return true;
          })
          .filter((f) => f.required)
          .map((f) => f.id);
      });

      const missing = requiredFields.filter((id) => !formData[id] && formData[id] !== 0);
      if (missing.length) console.warn("Missing required fields:", missing);

      onSubmit?.(formData);
    },
    [formConfig, formData, servicesEnabled, canteenEnabled, hostelEnabled, onSubmit]
  );

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{formTitle || formConfig[0]?.title || "Form"}</h1>

      <form onSubmit={handleSubmit} noValidate>
        {formConfig.map((section, index) => renderSection(section, index))}

        <div className={styles.btn}>
          <div></div>
          <Button type="submit" variant="primary">
            {submitLabel || "Submit"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default StudentForm;
